import { useQuery } from '@tanstack/react-query';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { IltCard } from 'src/api/generated/ApiClient';
import { getLiveTraining } from 'src/services/http/myLearning';
import { ISODateTimeString } from 'src/utils/date';
import { VALID_PRODUCTS } from 'src/constants';

const LIVE_TRAINING_QUERY_KEY = 'liveTraining';

/**
 * Converts a product string to camelCase format
 * Handles both space-separated and hyphen-separated strings
 */
const toCamelCase = (str: string): string =>
  str
    .toLowerCase()
    .split(/[\s-]+/) // Split on spaces or hyphens
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');

/**
 * Filters tags array to only include valid product names
 * @param tags - Array of tag strings from course data
 * @returns Array of valid product names (preserving original casing)
 */
const filterProductsFromTags = (tags: string[] | undefined): string[] => {
  if (!tags || tags.length === 0) {
    return [];
  }

  return tags.filter((tag) => {
    const camelCaseTag = toCamelCase(tag);

    return (VALID_PRODUCTS as readonly string[]).includes(camelCaseTag);
  });
};

export interface LiveTrainingSession {
  courseId: string;
  instanceId: string;
  title: string;
  subtitle: string;
  startDateTime: ISODateTimeString;
  endDateTime: ISODateTimeString;
  status: 'upcoming' | 'completed';
  enrolled: boolean;
  products: string[];
  host: string;
  showAddToCalendar: boolean;
  showDownloadMaterials: boolean;
  courseMaterialsUrl: string;
  videoCallUrl?: string;
  isFree: boolean;
  isSaved: boolean;
  isPrivate: boolean;
}

/**
 * Transforms ILT course data from the API into LiveTrainingSession format
 * @param course - ILT course data from the API
 * @returns Transformed LiveTrainingSession object
 */
const transformLiveTrainingData = (course: IltCard): LiveTrainingSession => {
  const session = course.sessionInfo;
  const isCompleted = course.enrollmentStatus === 'COMPLETED';
  const isPast = session?.endTime ? new Date(session.endTime) < new Date() : false;

  return {
    courseId: course.courseId || '',
    instanceId: session?.sessionId || '',
    title: course.courseName || '',
    subtitle: course.courseDescription || '',
    startDateTime: (session?.startTime || '') as ISODateTimeString,
    endDateTime: (session?.endTime || '') as ISODateTimeString,
    status: isCompleted ? 'completed' : 'upcoming',
    enrolled: course.enrollmentStatus === 'ENROLLED' || course.enrollmentStatus === 'COMPLETED',
    products: filterProductsFromTags(course.tags),
    host: session?.instructors?.[0] || '',
    showAddToCalendar: !isPast && !isCompleted,
    showDownloadMaterials: course.enrollmentStatus === 'ENROLLED' || course.enrollmentStatus === 'COMPLETED',
    courseMaterialsUrl: '',
    videoCallUrl: session?.meetingLink,
    isFree: course.isFree || false,
    isSaved: course.isBookmarked || false,
    isPrivate: course.isPrivate || false,
  };
};

/**
 * Custom hook to fetch live training data for the current user
 * @param limit - Maximum number of live training sessions to fetch (default: 20)
 * @param useMockData - Whether to use mock data instead of real API (default: false)
 * @returns Query result with live training sessions and loading state
 *
 * @example
 * const { data: liveTrainingSessions, isLoading } = useGetLiveTraining(20);
 *
 * @example
 * // Use mock data for development/testing
 * const { data: liveTrainingSessions, isLoading } = useGetLiveTraining(20, true);
 */
export const useGetLiveTraining = (limit: number = 20, useMockData: boolean = false) => {
  const { user, isLoading: isUserLoading } = useAtlassianUser();

  const query = useQuery({
    queryKey: [LIVE_TRAINING_QUERY_KEY, limit, user?.email, user?.account_id, useMockData],
    queryFn: async () => {
      // Return mock data if requested
      if (useMockData) {
        const { allTrainingSessions } = await import('src/pages/MyLearningPage/__fixtures__/liveTrainingDataMock');

        // Transform mock data to match LiveTrainingSession format
        return allTrainingSessions.map((session) => ({
          courseId: session.courseId,
          instanceId: session.instanceId,
          title: session.title,
          subtitle: session.subtitle,
          startDateTime: session.startDateTime,
          endDateTime: session.endDateTime,
          status: session.status,
          enrolled: session.enrolled,
          products: session.products,
          host: session.host,
          showAddToCalendar: session.showAddToCalendar,
          showDownloadMaterials: session.showDownloadMaterials,
          courseMaterialsUrl: session.courseMaterialsUrl,
          videoCallUrl: session.videoCallUrl,
          isFree: session.isFree,
          isSaved: session.isSaved,
          isPrivate: session.isPrivate,
        }));
      }

      if (!user?.email || !user?.account_id) {
        return [];
      }

      const response = await getLiveTraining(limit, user.email, user.account_id);

      return response?.courses?.map(transformLiveTrainingData) || [];
    },
    enabled: useMockData || (!!user?.email && !!user?.account_id),
    // Keep data fresh for navigation between routes
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    ...query,
    isLoading: useMockData ? false : isUserLoading || query.isLoading,
  };
};
